import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { apiErrorMessage } from '@/api/client'
import { billing as billingApi } from '@/api/endpoints'
import { Alert, Field, Input, Select, Spinner } from '@/components/ui'
import { COUNTRY_CODES, countryName } from '@/lib/countries'
import type { TenantBilling } from '@/types/api'

/**
 * Fakturačné údaje školy voči platforme.
 *
 * `Tenant` žije v registri, nie v databáze školy (`/api/billing`, viď
 * `apps/contracts/api.py`) — samostatná sekcia
 * nastavení s vlastným tlačidlom Uložiť; ukladá sa iný objekt než `Settings`.
 */
export default function BillingTab() {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['billing'], queryFn: billingApi.get })

  const [form, setForm] = useState<Partial<TenantBilling>>({})
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const save = useMutation({
    mutationFn: (payload: Partial<TenantBilling>) => billingApi.update(payload),
    onSuccess: (updated) => {
      setMessage({ kind: 'success', text: t('global.saved') })
      setForm(updated)
      void qc.invalidateQueries({ queryKey: ['billing'] })
    },
    onError: (err) => setMessage({ kind: 'error', text: apiErrorMessage(err) }),
  })

  if (isLoading || !data) {
    return (
      <section className="card flex h-40 items-center justify-center p-4 text-slate-400">
        <Spinner className="h-5 w-5" />
      </section>
    )
  }

  function set<K extends keyof TenantBilling>(key: K, value: TenantBilling[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const missing = form.missing ?? data.missing

  return (
    <section className="card max-w-3xl space-y-3 p-4">
      <div>
        <h2 className="text-sm font-semibold">{t('settings.billingTitle')}</h2>
        <p className="text-xs text-slate-500">{t('settings.billingHint')}</p>
      </div>

      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      {missing.length ? (
        <Alert variant="warning">
          {t('settings.billingMissing', { fields: missing.join(', ') })}
        </Alert>
      ) : (
        <Alert variant="success">{t('settings.billingComplete')}</Alert>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <Field label={t('settings.billingLegalName')} className="sm:col-span-2">
          <Input value={form.legal_name ?? ''} onChange={(e) => set('legal_name', e.target.value)} />
        </Field>
        <Field label={t('settings.billingStreet')} className="sm:col-span-2">
          <Input value={form.street ?? ''} onChange={(e) => set('street', e.target.value)} />
        </Field>
        <Field label={t('settings.billingPostalCode')}>
          <Input value={form.postal_code ?? ''} onChange={(e) => set('postal_code', e.target.value)} />
        </Field>
        <Field label={t('settings.billingCity')}>
          <Input value={form.city ?? ''} onChange={(e) => set('city', e.target.value)} />
        </Field>
        <Field label={t('settings.billingCountry')} hint={t('settings.billingCountryHint')}>
          <Select
            value={form.country_code ?? 'SK'}
            onChange={(e) => set('country_code', e.target.value)}
          >
            {COUNTRY_CODES.map((code) => (
              <option key={code} value={code}>
                {countryName(code, i18n.language)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('settings.billingVatId')}
          hint={
            data?.vat_verified === true
              ? t('settings.vatVerified', { name: data.vat_registered_name || data.vat_id })
              : data?.vat_verified === false
                ? t('settings.vatRejected')
                : undefined
          }
        >
          <Input value={form.vat_id ?? ''} onChange={(e) => set('vat_id', e.target.value)} />
        </Field>
        <Field label={t('settings.billingIco')}>
          <Input value={form.ico ?? ''} onChange={(e) => set('ico', e.target.value)} />
        </Field>
        <Field label={t('settings.billingDic')}>
          <Input value={form.dic ?? ''} onChange={(e) => set('dic', e.target.value)} />
        </Field>
        <Field label={t('settings.billingRegistration')} className="sm:col-span-2">
          <Input
            value={form.registration ?? ''}
            placeholder={t('settings.billingRegistrationPlaceholder')}
            onChange={(e) => set('registration', e.target.value)}
          />
        </Field>
        <Field label={t('settings.billingSignatoryName')}>
          <Input
            value={form.signatory_name ?? ''}
            onChange={(e) => set('signatory_name', e.target.value)}
          />
        </Field>
        <Field label={t('settings.billingSignatoryRole')}>
          <Input
            value={form.signatory_role ?? ''}
            onChange={(e) => set('signatory_role', e.target.value)}
          />
        </Field>
        <Field label={t('settings.billingEmail')}>
          <Input
            type="email"
            value={form.billing_email ?? ''}
            onChange={(e) => set('billing_email', e.target.value)}
          />
        </Field>
        <Field label={t('settings.billingPhone')}>
          <Input
            value={form.billing_phone ?? ''}
            onChange={(e) => set('billing_phone', e.target.value)}
          />
        </Field>
        <Field label={t('settings.billingIban')} className="sm:col-span-2">
          <Input value={form.iban ?? ''} onChange={(e) => set('iban', e.target.value)} />
        </Field>
      </div>

      <button
        type="button"
        className="btn-primary"
        disabled={save.isPending}
        onClick={() => {
          setMessage(null)
          save.mutate(form)
        }}
      >
        {save.isPending ? <Spinner /> : null}
        {t('global.save')}
      </button>
    </section>
  )
}
